import type { AnyItem, CompositePrimaryKey, DisplayTemplateAst, QuerySchemaWalker, ResolvedMonospaceCollection, ResolvedMonospacePrimitiveField } from '#layers/data-model/public';
import type { CalendarEventTiming } from '#layers/listing/constants/calendar';
import type { CalendarRange } from './build-calendar-range-filter';
import { Temporal } from 'temporal-polyfill';
import { getItemFieldValue, getItemPrimaryKey, getItemPrimaryKeyRouteString, isPrimitiveTypeDateTime, resolveDisplayTemplatePlainText, stringifyStableId } from '#layers/data-model/public';
import { CalendarEventTimings } from '#layers/listing/constants/calendar';

type CalendarEventEdge = Temporal.PlainDate | Temporal.ZonedDateTime;

export interface CalendarEventSpan {
	start: CalendarEventEdge;
	end: CalendarEventEdge;
}

export interface CalendarItemEvent extends CalendarEventSpan {
	id: string;
	primaryKey: CompositePrimaryKey;
	item: AnyItem;
	title: string;
	timing: CalendarEventTiming;
}

interface ToCalendarItemEventsOptions {
	items: readonly AnyItem[];
	collection: ResolvedMonospaceCollection;
	startField: ResolvedMonospacePrimitiveField;
	endField: ResolvedMonospacePrimitiveField | undefined;
	titleTemplate: DisplayTemplateAst | null;
	querySchema: QuerySchemaWalker | null | undefined;
	/** Every datetime moves into this zone, so all views show the same wall time. */
	timeZone: string;
	now: Temporal.ZonedDateTime;
}

export function toCalendarItemEvents({ items, collection, startField, endField, titleTemplate, querySchema, timeZone, now }: ToCalendarItemEventsOptions): CalendarItemEvent[] {
	return items.flatMap((item): CalendarItemEvent[] => {
		const start = toCalendarEdge(getItemFieldValue(item, startField), startField, timeZone);
		if (!start) return [];

		const primaryKey = getItemPrimaryKey(item, collection);
		const end = toCalendarEdge(getItemFieldValue(item, endField), endField, timeZone) ?? start;

		return [{
			id: stringifyStableId(primaryKey),
			primaryKey,
			item,
			title: resolveDisplayTemplatePlainText(titleTemplate, item, collection, querySchema) ?? getItemPrimaryKeyRouteString(item, collection),
			start,
			end,
			timing: getEventTiming({ start, end }, now),
		}];
	});
}

// Narrows values that RStore's field parser already turned into Temporal types.
function toCalendarEdge(value: unknown, field: ResolvedMonospacePrimitiveField | undefined, timeZone: string): CalendarEventEdge | null {
	if (value == null || !field) return null;

	if (isPrimitiveTypeDateTime(field.type)) {
		if (value instanceof Temporal.ZonedDateTime) return value.withTimeZone(timeZone);
		if (value instanceof Temporal.PlainDateTime) return value.toZonedDateTime(timeZone);
		return null;
	}

	return value instanceof Temporal.PlainDate ? value : null;
}

// Compares date items by day, so one that starts or ends today counts as live.
function getEventTiming({ start, end }: CalendarEventSpan, now: Temporal.ZonedDateTime): CalendarEventTiming {
	if (compareToNow(end, now) < 0) return CalendarEventTimings.Past;
	return compareToNow(start, now) <= 0 ? CalendarEventTimings.Live : CalendarEventTimings.Upcoming;
}

function compareToNow(edge: CalendarEventEdge, now: Temporal.ZonedDateTime) {
	if (edge instanceof Temporal.PlainDate) return Temporal.PlainDate.compare(edge, now.toPlainDate());
	return Temporal.ZonedDateTime.compare(edge, now);
}

/** Marks date items and timed items that end on a later date, which schedule-x draws apart from one-day timed items. */
export function isSpanning({ start, end }: CalendarEventSpan): boolean {
	if (start instanceof Temporal.PlainDate || end instanceof Temporal.PlainDate) return true;
	return !start.toPlainDate().equals(end.toPlainDate());
}

export function toPlainDate(edge: CalendarEventEdge): Temporal.PlainDate {
	return edge instanceof Temporal.ZonedDateTime ? edge.toPlainDate() : edge;
}

interface EventsInRangeOptions<T extends CalendarEventSpan> {
	events: readonly T[];
	range: CalendarRange;
}

/** Maps each ISO date in `range` to the events on it, as schedule-x's month grid places them. */
export function assignEventsToDays<T extends CalendarEventSpan>({ events, range }: EventsInRangeOptions<T>): Map<string, T[]> {
	const days = new Map<string, T[]>();

	for (const event of events) {
		const span = getDaysInRange(event, range);
		if (!span) continue;

		for (let day = span.firstDay; Temporal.PlainDate.compare(day, span.lastDay) <= 0; day = day.add({ days: 1 })) {
			const key = day.toString();
			const dayEvents = days.get(key);

			if (dayEvents) dayEvents.push(event);
			else days.set(key, [event]);
		}
	}

	return days;
}

function getDaysInRange(event: CalendarEventSpan, range: CalendarRange) {
	const startDate = toPlainDate(event.start);
	const endDate = toPlainDate(event.end);

	const firstDay = Temporal.PlainDate.compare(startDate, range.start) < 0 ? range.start : startDate;
	const lastDay = Temporal.PlainDate.compare(endDate, range.end) > 0 ? range.end : endDate;
	if (Temporal.PlainDate.compare(lastDay, firstDay) < 0) return null;

	return { firstDay, lastDay };
}
